import { ComponentProps, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpSelect } from 'new-components/DLS/OpSelect/OpSelect';
import { OpRangePicker } from 'new-components/DLS/OpRangePicker/OpRangePicker';
import { OpCol } from 'new-components/DLS/OpCol/OpCol';
import { OpCollapse } from 'new-components/DLS/OpCollapse/OpCollapse';
import { OpRow } from 'new-components/DLS/OpRow/OpRow';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { getOpParams } from 'utils/getOpParams';
import useIsUsingDarkTheme from 'utils/customHooks/useIsUsingDarkTheme';

import { DATE_TIME_FORMAT } from '../../constants/dates';
import {
  describeLocation,
  describeLocationWorkflow,
  fetchLocations,
  LocationsState,
} from '../../store/slices/locationsSlice';
import { AppDispatch, ImmutableRootState } from '../../store/store';

dayjs.extend(utc);
dayjs.extend(timezone);

interface DateRangeLocationFilterProps {
  onDateRangeLocationFilter: (
    locationId: number,
    startDate: string,
    endDate: string,
  ) => void;
  initialStartDate: string;
  initialEndDate: string;
}

const DateRangeLocationFilter2 = ({
  onDateRangeLocationFilter,
  initialStartDate,
  initialEndDate,
}: DateRangeLocationFilterProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const isDarkMode = useIsUsingDarkTheme();
  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as NonNullable<LocationsState['globalLocation']>['id'] | undefined;
  const globalLocation = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation']),
  ) as LocationsState['globalLocation'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];
  const fetchLocationsLoading = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'fetchLocationsLoading']),
  ) as LocationsState['fetchLocationsLoading'];

  const [dateRange, setDateRange] = useState<(string | null)[] | null>([
    dayjs(initialStartDate).toISOString(),
    dayjs(initialEndDate).toISOString(),
  ]);

  // Fetch locations
  useEffect(() => {
    if (orgId && locations.length === 0) {
      dispatch(fetchLocations({ orgId, status: 1 }));
    }
  }, [dispatch, orgId, locations.length]);

  const handleUpdate = () => {
    if (globalLocationId && dateRange?.[0] && dateRange?.[1]) {
      let formattedStartDate: string;
      let formattedEndDate: string;

      // For "All Locations", format without timezone (will be interpreted by each location's timezone in saga)
      // For specific location, extract date string and apply location timezone
      if (globalLocationId === -1) {
        formattedStartDate = dayjs(dateRange[0])
          .startOf('day')
          .format(DATE_TIME_FORMAT);
        formattedEndDate = dayjs(dateRange[1])
          .endOf('day')
          .format(DATE_TIME_FORMAT);
      } else {
        // Extract date string only (without timezone info) and parse it in location timezone
        const startDateStr = dayjs(dateRange[0]).format('YYYY-MM-DD');
        const endDateStr = dayjs(dateRange[1]).format('YYYY-MM-DD');

        formattedStartDate = dayjs
          .tz(startDateStr, tz)
          .startOf('day')
          .format(DATE_TIME_FORMAT);
        formattedEndDate = dayjs
          .tz(endDateStr, tz)
          .endOf('day')
          .format(DATE_TIME_FORMAT);
      }

      onDateRangeLocationFilter(
        globalLocationId,
        formattedStartDate,
        formattedEndDate,
      );
      notificationService.show(
        'success',
        t('dateRangeLocationFilter.filterSuccess'),
      );
    }
  };

  const handleLocationChange = async (locationId: number) => {
    await dispatch(describeLocation({ orgId, locationId, global: true }));
    await dispatch(
      describeLocationWorkflow({ orgId, locationId, global: true }),
    );
  };

  // Disabled date by month
  const disabledByMonth: ComponentProps<
    typeof OpRangePicker
  >['disabledDate'] = (current, { from }) => {
    if (from) {
      return current.isAfter(from.add(1, 'month'));
    }
    return false;
  };

  const tz =
    globalLocation?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Anchor presets to the location's current calendar date, but as a
  // timezone-naive (browser-local) dayjs so the date-only RangePicker doesn't
  // roll the end-of-period boundary into the next day when the browser timezone
  // differs from the location's (e.g. an LA location viewed from KST). The
  // actual query re-applies the location timezone in handleUpdate.
  const now = dayjs(dayjs().tz(tz).format('YYYY-MM-DD'));

  const rangePresets: NonNullable<
    ComponentProps<typeof OpRangePicker>['presets']
  > = () => [
    {
      label: t('dateRangeLocationFilter.thisWeek'),
      value: () =>
        [now.startOf('week'), now.endOf('week')] as [dayjs.Dayjs, dayjs.Dayjs],
    },
    {
      label: t('dateRangeLocationFilter.nextWeek'),
      value: () =>
        [
          now.add(1, 'week').startOf('week'),
          now.add(1, 'week').endOf('week'),
        ] as [dayjs.Dayjs, dayjs.Dayjs],
    },
    {
      label: t('dateRangeLocationFilter.lastWeek'),
      value: () =>
        [
          now.subtract(1, 'week').startOf('week'),
          now.subtract(1, 'week').endOf('week'),
        ] as [dayjs.Dayjs, dayjs.Dayjs],
    },
    {
      label: t('dateRangeLocationFilter.thisMonth'),
      value: () =>
        [now.startOf('month'), now.endOf('month')] as [
          dayjs.Dayjs,
          dayjs.Dayjs,
        ],
    },
    {
      label: t('dateRangeLocationFilter.lastMonth'),
      value: () =>
        [
          now.subtract(1, 'month').startOf('month'),
          now.subtract(1, 'month').endOf('month'),
        ] as [dayjs.Dayjs, dayjs.Dayjs],
    },
  ];

  return (
    <OpCollapse
      defaultActiveKey={['1']}
      items={[
        {
          key: '1',
          label: t('dateRangeLocationFilter.label'),
          children: (
            <OpRow gutter={[16, 16]} align="middle" style={{ width: '100%' }}>
              <OpCol xs={24} sm={24} md={4} lg={6}>
                <OpSelect
                  key={String(isDarkMode)}
                  value={
                    fetchLocationsLoading
                      ? undefined // Show nothing if loading
                      : globalLocationId === -1 // Check for "All Locations"
                        ? -1
                        : locations.find(
                            (location) => location.id === globalLocationId,
                          )?.id // Ensure the location id matches
                  }
                  style={{ width: '100%' }}
                  options={
                    locations.length > 1
                      ? [
                          {
                            label: t('dateRangeLocationFilter.allLocations'),
                            value: -1,
                          },
                          ...locations.map((location) => ({
                            value: location.id,
                            label: location.name,
                          })),
                        ]
                      : locations.map((location) => ({
                          value: location.id,
                          label: location.name,
                        }))
                  }
                  onChange={handleLocationChange}
                  loading={fetchLocationsLoading}
                  placeholder={t('dateRangeLocationFilter.selectLocation')}
                />
              </OpCol>

              <OpCol xs={24} sm={24} md={16} lg={14}>
                <OpRangePicker
                  key={String(isDarkMode)}
                  showTime={false}
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                  presets={rangePresets}
                  disabledDate={disabledByMonth}
                />
              </OpCol>

              <OpCol xs={24} sm={24} md={4} lg={4}>
                <OpButton
                  type="primary"
                  style={{ width: '100%' }}
                  onClick={handleUpdate}
                >
                  {t('dateRangeLocationFilter.update')}
                </OpButton>
              </OpCol>
            </OpRow>
          ),
        },
      ]}
    />
  );
};

export default DateRangeLocationFilter2;
